import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  buildFormFieldFromMeta,
  normaliseRegistrationField,
  useForm,
  type FormContext,
} from '@/shared/lib/forms'
import irisApi from '../lib/api'
import { scrollToEntityField } from '../lib/form-field-errors'
import type { UploadSession } from '../types'

export function IrisEntityForm({
  token,
  session,
  formRef,
  onUploadsChange,
  navRef,
}: {
  token: string
  session: UploadSession
  formRef: RefObject<FormContext | null>
  /** +1 when a photo upload starts, -1 when it settles. */
  onUploadsChange?: (delta: number) => void
  /** Set to "show this question": opens its section, then scrolls to it. */
  navRef?: RefObject<((code: string) => void) | null>
}) {
  const { t } = useTranslation()
  // Sections that ask something, in order. With two or more, the form shows
  // one at a time.
  const sections = useMemo(() => {
    const used = new Set((session.form_fields ?? []).map((f) => f.section ?? ''))
    return (session.form_sections ?? []).filter((s) => used.has(s.key))
  }, [session.form_fields, session.form_sections])
  const stepped = sections.length > 1
  const [current, setCurrent] = useState(0)
  // Read by each field's `visible` check, which the form evaluates on render.
  const currentRef = useRef(0)
  const [pendingCode, setPendingCode] = useState<string | null>(null)

  const fields = useMemo(() => {
    const fieldsMeta = session.form_fields ?? []
    const values = session.form_values ?? {}
    return fieldsMeta.map((raw) => {
      const meta = normaliseRegistrationField({
        uuid: raw.uuid,
        code_name: raw.code_name,
        display_name: raw.display_name,
        field_type: raw.field_type,
        required: raw.required,
        hint: raw.hint,
        description: raw.description,
        options: raw.options,
        validation_rules: raw.validation_rules as never,
        configuration_data: raw.configuration_data,
        order: raw.order,
      })
      const field = buildFormFieldFromMeta(meta, values[raw.code_name], 'submit')
      if (!stepped) return field
      // A question outside the open section is hidden, so it is checked
      // when its own section is open, not before.
      const key = raw.section ?? ''
      return { ...field, visible: () => sections[currentRef.current]?.key === key }
    })
  }, [session.form_fields, session.form_values, sections, stepped])

  const { Form, ref } = useForm(
    {
      name: 'iris-entity-form',
      submit: () => <></>,
      fields,
      uploadFile: async ({ file, fieldName }) => {
        const form = new FormData()
        form.append('file', file)
        if (fieldName) form.append('field_code', fieldName)
        onUploadsChange?.(1)
        try {
          const res = await irisApi.post(`/${token}/form-files/`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          const stored = res.data as {
            uuid?: string
            original_filename?: string
            url?: string
          }
          return {
            uuid: stored.uuid,
            file_name: stored.original_filename,
            file_url: stored.url,
          }
        } finally {
          onUploadsChange?.(-1)
        }
      },
    },
    async () => undefined,
    [token, onUploadsChange],
  )

  useEffect(() => {
    formRef.current = ref.current
    return () => {
      formRef.current = null
    }
  }, [formRef, ref])

  const goTo = useCallback((index: number) => {
    currentRef.current = index
    setCurrent(index)
    document
      .querySelector('[data-testid="iris-entity-form"]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  // A question to show once its section has rendered.
  useEffect(() => {
    if (pendingCode === null) return
    scrollToEntityField(pendingCode)
    setPendingCode(null)
  }, [pendingCode, current])

  useEffect(() => {
    if (!navRef) return
    navRef.current = (code: string) => {
      const section = (session.form_fields ?? []).find((f) => f.code_name === code)?.section
      const index = sections.findIndex((s) => s.key === section)
      if (stepped && index >= 0 && index !== currentRef.current) goTo(index)
      setPendingCode(code)
    }
    return () => {
      navRef.current = null
    }
  }, [navRef, session.form_fields, sections, stepped, goTo])

  const next = async () => {
    const form = ref.current
    if (!form) return
    // Checks the open section only: the others are hidden.
    await form.submitForm()
    const first = Object.keys(await form.validateForm())[0]
    if (first) {
      scrollToEntityField(first)
      return
    }
    goTo(current + 1)
  }

  if (fields.length === 0) return null
  const section = stepped ? sections[current] : sections[0]

  return (
    <div className="mb-8 scroll-mt-6" data-testid="iris-entity-form">
      {section ? (
        <div className="mb-4" data-testid="iris-form-section">
          {stepped ? (
            <p className="clara-small mb-1 uppercase tracking-[0.14em] text-[var(--ink-3)]">
              {t('upload.form.sectionOf', 'Section {{current}} of {{total}}', {
                current: current + 1,
                total: sections.length,
              })}
            </p>
          ) : null}
          <h2 className="clara-heading text-[18px]">{section.title}</h2>
          {section.description ? (
            <p className="clara-body mt-1 text-[var(--ink-2)]">{section.description}</p>
          ) : null}
        </div>
      ) : null}
      <Form className="flex flex-col gap-3" />
      {stepped ? (
        <div className="mt-5 flex items-center gap-3">
          {current > 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => goTo(current - 1)}
              data-testid="iris-form-back"
            >
              <ArrowLeft size={14} />
              {t('common.back', 'Back')}
            </Button>
          ) : null}
          {current < sections.length - 1 ? (
            <Button
              type="button"
              className="ml-auto"
              onClick={() => void next()}
              data-testid="iris-form-next"
            >
              {t('common.next', 'Next')}
              <ArrowRight size={14} />
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
